import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Lock, CheckCircle, Loader, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import Logo from '../../components/common/Logo'

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const emailFromQuery = searchParams.get('email')
  const otpsIdFromQuery = searchParams.get('otps_id')

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long')
      return
    }

    // Require email and otps_id to call backend reset endpoint
    const email = emailFromQuery
    const otps_id = otpsIdFromQuery ? parseInt(otpsIdFromQuery, 10) : null

    if (!email || !otps_id) {
      toast.error('Missing reset context. Please request a new password reset link or retry the forgot-password flow.')
      return
    }

    setIsLoading(true)

    try {
      const { authService } = await import('../../services/authService')
      const res = await authService.resetPasswordWithOtp({
        email,
        password: formData.password,
        confirm_password: formData.confirmPassword,
        otps_id
      })

      if (res && res.success) {
        setIsSuccess(true)
        toast.success(res.message || 'Password reset successfully!')
      } else {
        toast.error(res?.message || 'Failed to reset password. Please try again.')
      }
    } catch (error) {
      console.error('Reset password error', error)
      toast.error(error.message || 'Failed to reset password. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }



  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-sm card p-6 shadow-xl"
        >
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-success-600 dark:text-success-400" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Password Reset!</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Your password has been updated. You can now sign in.
            </p>
            <div className="pt-2">
              <Link
                to="/auth/login"
                className="btn btn-primary w-full py-2 text-sm"
              >
                Sign In
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm"
      >
        <div className="card p-6 shadow-xl space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <div className="w-10 h-10">
                <Logo sizeClass="w-10 h-10" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Reset Password</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Enter your new password below
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="password" className="label text-xs">
                New password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input pl-9 pr-9 py-2 text-sm"
                  placeholder="New password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="confirmPassword" className="label text-xs">
                Confirm
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="input pl-9 pr-9 py-2 text-sm"
                  placeholder="Confirm password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-error-500 text-xs mt-1">Passwords do not match</p>
              )}
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg">
              <p className="text-[10px] items-center font-medium text-slate-500 mb-1.5">REQUIREMENTS</p>
              <ul className="space-y-1">
                <li className={`flex items-center text-xs ${formData.password.length >= 8 ? 'text-success-600 dark:text-success-400' : 'text-slate-500 dark:text-slate-400'}`}>
                  <CheckCircle className="w-3 h-3 mr-1.5" />
                  8+ chars
                </li>
                <li className={`flex items-center text-xs ${/[A-Z]/.test(formData.password) ? 'text-success-600 dark:text-success-400' : 'text-slate-500 dark:text-slate-400'}`}>
                  <CheckCircle className="w-3 h-3 mr-1.5" />
                  One Uppercase
                </li>
                <li className={`flex items-center text-xs ${/[a-z]/.test(formData.password) ? 'text-success-600 dark:text-success-400' : 'text-slate-500 dark:text-slate-400'}`}>
                  <CheckCircle className="w-3 h-3 mr-1.5" />
                  One Lowercase
                </li>
                <li className={`flex items-center text-xs ${/\d/.test(formData.password) ? 'text-success-600 dark:text-success-400' : 'text-slate-500 dark:text-slate-400'}`}>
                  <CheckCircle className="w-3 h-3 mr-1.5" />
                  One Number
                </li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={isLoading || formData.password !== formData.confirmPassword || formData.password.length < 8}
              className="btn btn-primary w-full py-2.5 shadow-lg shadow-primary-500/20 group text-sm"
            >
              {isLoading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                  Resetting...
                </>
              ) : (
                <>
                  Reset password
                </>
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  )
}

export default ResetPasswordPage
